<template lang="pug">
  el-row#placeholder-container-admin(
    :class="[questionData.isSelected  ? 'placeholder-selected': 'placeholder-normal']"
   )
    el-row(
      @mouseover.native="isHover=true"
      @mouseleave.native="isHover=false"
    ) 
      // Question Header
      div#question-header 
        el-col#question-text(:span="20") 
          el-checkbox(
            v-if="selectOnly"
            size="medium" 
            v-model="questionData.isSelected"
            style="margin-right: 10px;"
            @change="$emit('questionSelected', id, questionData.isSelected)")
          img#question-type-icon(:src="typeIcon")
          span {{ questionData.title }}
      
        el-col(v-if="questionData.score>0" :span="4") 
          div#score-text {{ questionData.score + ' ' }}
            span(v-if="questionData.score>=2") pts
            span(v-else) pt

      // Edit/Delete Buttons
      div#question-edit-buttons(v-if="!selectOnly")
        el-button-group(:style="{ visibility: isHover ? 'visible' : 'hidden' }") 
          el-button(
            icon="el-icon-delete"
            @click="hide")   
          el-button(
            icon="el-icon-edit"
            @click="$emit('editQuestion', id)")    
      
      // Question Text
      div#placeholder-content
        p.ql-editor(
          style="padding: 0px;"
          v-html="questionData.text"
        )

      // Question Custom Content
      component(
        :is="previewType"
        :questionData="questionData"
        :contentVisible="showPreview"
        :tagsList="tagsList"
      )

    // Tagging UI
    el-row(v-if="superAccess || showTags" style="border-top: 1px solid #dcdfe6; padding: 1%")
      el-col(:span="24" v-if="superAccess")
        el-select#question-tag-select(
          v-if="isTagInputVisible"
          filterable
          placeholder='New Tag'
          v-model="newTag"
          @change="selectTag"
        )
          el-option#question-tag-options(
            v-for="item in tagsList"
            :key="item.id"
            :label="item.text"
            :value="item.text"
          )
        el-button#question-tag-button(
          v-if="!isTagInputVisible"
          class="normal-button" 
          size="small"
          @click="isTagInputVisible=true") 
            small + Question Tag
        el-tag#question-tag-button(
          v-for="tag in activeTags"
          :key="tag"
          closable
          type="success"
          @close="removeTag(tag)") {{tag}}
      el-col(:span="24" v-else-if="showTags")
        el-tag#question-tag-button(
          key="tag_label"
          type="success"
        ) Tags:
        el-tag#question-tag-button(
          v-for="tag in activeTags"
          :key="tag"
          type="info") {{tag}}
</template>

<script>
import AnswerInputPreview from '@/components/instructor/Question/Placeholder/AnswerInputPreview'
import CodingPreview from '@/components/instructor/Question/Placeholder/CodingPreview'
import SelectPreview from '@/components/instructor/Question/Placeholder/SelectPreview'
import utilMixin from '@/components/mixins/utilMixin'
import {SUPER_QUIZ_ID, QUESTION_TYPES} from '@/defines/constants'

export default {
  name: 'QuestionPlaceholder',

  components: {
    AnswerInputPreview,
    CodingPreview,
    SelectPreview,
  },

  mixins: [utilMixin],

  props: {
    id: {
      type: Number,
      required: true,
    },

    questionData: {
      type: Object,
      required: true,
    },

    showPreview: {
      type: Boolean,
      required: true,
    },

    selectOnly: {
      type: Boolean,
      required: true,
    },

    superAccess: {
      type: Boolean,
      default: false,
    },
    
    showTags: {
      type: Boolean,
      default: false,
    },

    tagsList: {
      type: Array,
      default: () => { return [] },
    },

    tagUpdate: {
      type: Object,
      default: () => { return {id: null, text: null} },
    },
  },

  data() {
    return {
      visible: 'visible',
      isHover: false,
      content: 'Here is a sample Answer-Input question. Select the pencil icon to make it your own.',
      newTag: '',
      isTagInputVisible: false,
      activeTags: [],
      firstLoaded: true,
      typeIcon: null,

      previewTypes: [
        'None',
        'AnswerInputPreview',
        'CodingPreview',
        'SelectPreview',
      ],
    }
  },

  computed: {
    previewType() {
      return this.previewTypes[this.questionData.type_id]
    },
  },

  watch: {
    tagUpdate() {
      if ( this.tagUpdate.id !== null && this.activeTags.length > 0 ) {
        if ( this.tagUpdate.id === -1 ) { // Delete Case
          for (let idx=0; idx<this.activeTags.length; idx++) {
            if ( this.activeTags[idx] === this.tagUpdate.text) {
              this.activeTags.splice(idx,1)
              this.questionData.tags.splice(idx,1)
              break
            }
          }
        }
        else { // Update Case
          for (let idx=0; idx<this.questionData.tags.length; idx++) {
            if ( this.tagUpdate.id  === this.questionData.tags[idx]) {
              this.activeTags[idx] = this.tagUpdate.text
              break
            }
          }
        }
      }
    },

    questionData() {
      if ( this.superAccess || this.showTags) {
        this.updateTagView()
      }
      const typeKey = Object.keys(QUESTION_TYPES).find(key => QUESTION_TYPES[key] === this.questionData.type_id)
      this.typeIcon = require(`@/assets/QuestionTypes/${typeKey}_Icon.svg`)
    },
  },

  mounted() {
    if ( this.superAccess || this.showTags) {
      this.updateTagView()
    }
    this.firstLoaded = false
    const typeKey = Object.keys(QUESTION_TYPES).find(key => QUESTION_TYPES[key] === this.questionData.type_id)
    this.typeIcon = require(`@/assets/QuestionTypes/${typeKey}_Icon.svg`)
  },

  methods: {
    hide() {
      this.$msgbox( {
          title: 'Delete Question',
          message: 'Do you want to delete <strong>' + this.questionData.title + '</strong>?',
          showCancelButton: true,
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          dangerouslyUseHTMLString: true,
          confirmButtonClass: 'delete-button',
        })
          .then(() => {
             this.$emit('hideItem', this.id)
          })
          .catch(() => { })
     
    },

    getTagId(tag) {
      let i = 0
      for (i=0; i<this.tagsList.length; i += 1) {
        if (this.tagsList[i].text === tag) {
          break
        }
      }
      return this.tagsList[i].id
    },

    getTagText(tagId) {
      let i = 0
      for (i=0; i<this.tagsList.length; i += 1) {
        if (this.tagsList[i].id === tagId) {
          break
        }
      }

      return (i < this.tagsList.length) ? this.tagsList[i].text : ''
    },

    removeTag(tag) {
      
      let tagId = this.getTagId(tag)

      if (tagId !== undefined) {
        for( let idx=0; idx<this.activeTags.length; idx++) {
          if ( this.activeTags[idx] === tag ) {
            this.activeTags.splice(idx,1)
            break
          }
        }

        this.axios.delete('/api/quiz/' + SUPER_QUIZ_ID + '/question/' + this.questionData.id + '/tag/' + tagId)
        .then((result) => {
        })
        .catch((error) => {
          this.activeTags.push(tag)
          this.mNotification({error: error, message: 'Error encountered deleting your tag.'})
        })
      }
    },

    selectTag(tag) {
      let duplicated = false
      for( let idx=0; idx<this.activeTags.length; idx++) {
        if ( this.activeTags[idx] === tag ) {
          duplicated = true
          break
        }
      }

      if (!duplicated) {
        this.activeTags.push(tag)
        this.axios.post('/api/quiz/' + SUPER_QUIZ_ID + '/question/' + this.questionData.id + '/tag', {
          questionId: this.questionData.id,
          tag: this.getTagId(tag),
        })
        .then((result) => {
          if (this.questionData.tags === null || this.questionData.tags === undefined) {
            this.questionData.tags = []
          }
          this.questionData.tags.push(this.getTagId(tag))
         })
         .catch((error) => {
          for( let idx=0; idx<this.activeTags.length; idx++) {
            if ( this.activeTags[idx] === tag ) {
              this.activeTags.splice(idx,1)
              break
            }
          }
          this.mNotification({error: error, message: 'Error encountered saving your tag.'})
        })
      }
      this.isTagInputVisible = false
      this.newTag = ''
    },

    updateTagView() {
      this.activeTags = []
      if (this.questionData.tags !== undefined && this.questionData.tags !== null) {
        this.questionData.tags.forEach((item, index) => {
          this.activeTags.push(this.getTagText(item))
        })
      }
    },
  },
}
</script>

<style module>
  #placeholder-container-admin {
    margin: 2%;
    border-radius: 7px;
    height: auto;
  }
  
  #placeholder-container-admin:hover {
    background-color: #e3f2fd;
    border: 1px dotted #1e88e5;
    cursor: pointer;
  }

  .placeholder-normal {
    border: 1px solid #b0b2b5;
    background-color: #fff;
  }

  .placeholder-selected {
    background-color: #f0f9eb;
    border: 1px dotted #1e88e5;
  }

  #question-header {
    min-height: 40px;
    font-size: 16px;
    font-weight: bold;
    margin-top: 20px;
  }  

  #question-text {
    padding-left: 25px;
    font-size: 1.2em;
    color: #00a8ff;
  }

  #score-text {
    margin-right: 20px;
    float: right;
  }

  #placeholder-content {
    margin: 10px 30px 30px 30px;
    font-size: 15px;
  }

  #question-edit-buttons {
    float: right;
    margin: 0px 10px 10px 20px;
  }

  #question-tag-select {
    font-size: 12px;
    height: 32px;
    width: 100px;
    margin-left: 5px;
    margin-bottom: 5px;
  }
  
  #question-tag-button {
    margin-left: 5px;
    margin-bottom: 5px;
  }

  #question-tag-options {
    font-size: 14px;
    height: 35px;
  }

  #question-type-icon {
    width: 30px;
    height: 30px;
    padding-right: 15px;
    vertical-align: text-bottom;
  }
</style>
